import {
  Button,
  ButtonIconWithLabel,
  Icon_OpenNew24,
  Icon_TriangleDown,
  Menu,
  Spinner,
} from "@shapr3d/frontend-lib";
import toast from "react-hot-toast";

import { defaultPrivateSpaceName, defaultTeamSpaceName } from "../../../config";
import { useProject } from "../../../hooks/useProject";
import { useProjectActions } from "../../../hooks/useProjectActions";
import { useTracker } from "../../../hooks/useTracker";
import useTranslation from "../../../hooks/useTranslation";
import { useWebExport } from "../../../hooks/useWebExport";
import { ProjectRoleUtils } from "../../../types";
import { formatDate } from "../../../utils/helpers";
import { HeaderBreadCrumbs } from "../../HeaderBreadCrumbs/HeaderBreadCrumbs";
import { UserName } from "../../UserName/UserName";

import style from "./ProjectMetadata.module.css";

const contextMenuLocation = "project_page_project_title";
const spaceTypeNameMap: Record<string, string> = {
  private: defaultPrivateSpaceName,
  team: defaultTeamSpaceName,
};

const ProjectMetadataSection = () => {
  const { data: projectData } = useProject();
  const tracker = useTracker();
  const { t, m } = useTranslation();
  const { handleEditInAppClick, handleCopyProjectLink, handleShareClick, handleDownloadClick } =
    useProjectActions();

  if (!projectData) {
    return null;
  }

  const showEditInAppButton = ProjectRoleUtils.isAtLeast(projectData.userRole, "editor");
  const canDownload = Boolean(
    projectData.latestRevision.fileDownload?.url && projectData.project.projectId
  );
  const projectSpaceName =
    projectData.space?.spaceName ?? spaceTypeNameMap[projectData.space?.spaceType ?? ""];
  const projectCreatedByUser = projectData.users?.find(
    (user) => user.userId === projectData.latestRevision.uploadedByUserId
  );

  const handleMetadataMenuOpen = () => {
    tracker.trackContextMenuOpened({
      location: contextMenuLocation,
    });
  };

  const handleMetadataCopyLink = async () => {
    tracker.trackContextMenuActed({
      action: "copy_link",
      location: contextMenuLocation,
    });

    const isCopied = await handleCopyProjectLink();
    if (isCopied) {
      toast.success(t(m.ProjectSidebar.CopiedToClipboard));
    } else {
      toast.error(t(m.ProjectSidebar.FailedToCopyLink));
    }
  };

  const handleMetadataShare = () => {
    tracker.trackContextMenuActed({
      action: "share",
      location: contextMenuLocation,
    });

    handleShareClick();
  };

  const handleMetadataDownload = () => {
    tracker.trackContextMenuActed({
      action: "download",
      location: contextMenuLocation,
    });

    handleDownloadClick();
  };

  return (
    <>
      <div className={style.projectData}>
        {projectSpaceName && (
          <div className={style.breadcrumbs}>
            <HeaderBreadCrumbs teamID={projectData.teamId} spaceName={projectSpaceName} />
          </div>
        )}
        <Menu
          className={style.menuWrapper}
          activator={
            <div role="button" className={style.title} title={projectData.project.name}>
              <span className={style.titleText}>{projectData.project.name}</span>
              <Icon_TriangleDown className={style.triangleDown} />
            </div>
          }
          onOpen={handleMetadataMenuOpen}
        >
          {showEditInAppButton && (
            <Menu.Item closeOnClick onClick={handleEditInAppClick}>
              <span>{t(m.ProjectSidebar.EditInApp)}</span>
            </Menu.Item>
          )}
          <Menu.Item closeOnClick onClick={handleMetadataCopyLink}>
            <span>{t(m.ProjectSidebar.CopyLink)}</span>
          </Menu.Item>
          {showEditInAppButton && (
            <Menu.Item closeOnClick onClick={handleMetadataShare}>
              <span>{t(m.ProjectSidebar.Share)}</span>
            </Menu.Item>
          )}
          {canDownload && (
            <Menu.Item closeOnClick onClick={handleMetadataDownload}>
              <span>{t(m.ProjectSidebar.DownloadAsShapr)}</span>
            </Menu.Item>
          )}
        </Menu>
        <div>
          <div className={style.lastEdited}>
            {t(m.ProjectSidebar.LastEdited, {
              date: formatDate(projectData.latestRevision.lastModifiedAt),
            })}
          </div>
          <UserName user={projectCreatedByUser} fallback={"-"} color="gray" />
        </div>
        {showEditInAppButton && (
          <Button
            variant="grayscale"
            className={style.editInAppButton}
            onClick={handleEditInAppClick}
          >
            <ButtonIconWithLabel icon={<Icon_OpenNew24 />} label={t(m.ProjectSidebar.EditInApp)} />
          </Button>
        )}
      </div>
      <div className={style.divider}></div>
    </>
  );
};

const WebExportMetadataSection = () => {
  const { data: webExportData } = useWebExport();
  const tracker = useTracker();
  const { t, m } = useTranslation();
  const { handleCopyViewLink } = useProjectActions();

  if (!webExportData) {
    return null;
  }

  const webExport = webExportData.webExport;

  const handleMetadataMenuOpen = () => {
    tracker.trackContextMenuOpened({
      location: contextMenuLocation,
    });
  };

  const handleMetadataCopyLink = async () => {
    tracker.trackContextMenuActed({
      action: "copy_link",
      location: contextMenuLocation,
    });

    const isCopied = await handleCopyViewLink();
    if (isCopied) {
      toast.success(t(m.ProjectSidebar.CopiedToClipboard));
    } else {
      toast.error(t(m.ProjectSidebar.FailedToCopyLink));
    }
  };

  return (
    <>
      <div className={style.projectData}>
        <Menu
          className={style.menuWrapper}
          activator={
            <div role="button" className={style.title} title={webExport.title}>
              <span className={style.titleText}>{webExport.title}</span>
              <Icon_TriangleDown className={style.triangleDown} />
            </div>
          }
          onOpen={handleMetadataMenuOpen}
        >
          <Menu.Item closeOnClick onClick={handleMetadataCopyLink}>
            <span>{t(m.ProjectSidebar.CopyLink)}</span>
          </Menu.Item>
        </Menu>

        <div>
          <div className={style.lastEdited}>
            {t(m.ProjectSidebar.Published, { date: formatDate(webExport.uploadedAt) })}
          </div>
        </div>
      </div>
      <div className={style.divider}></div>
    </>
  );
};

export const ProjectMetadata = () => {
  const { data: projectData, isLoading: isProjectLoading } = useProject();
  const { data: webExportData, isLoading: isWebExportLoading } = useWebExport();

  const isLoading = !projectData && !webExportData && (isProjectLoading || isWebExportLoading);

  if (isLoading) {
    return (
      <>
        <div className={style.loadingContainer}>
          <Spinner size={20} />
        </div>
        <div className={style.divider}></div>
      </>
    );
  }

  if (projectData) {
    return <ProjectMetadataSection />;
  }

  if (webExportData) {
    return <WebExportMetadataSection />;
  }

  return null;
};
